import { Chip, Link, Stack } from '@mui/material';
import { DataGridProProps } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { MenuCell } from '../../../common/components/Cells/MenuCell';
import { TagCell } from '../../../common/components/Cells/TagCell';
import { FilterValueInputType } from '../../../common/components/FilterPanel/enum/FilterValueInputType';
import { Operation } from '../../../common/components/FilterPanel/enum/Operation';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../../common/controllers/PaginatedTableController';
import { AuthorizationError } from '../../../common/errors/errors';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { AppConfigContext } from '../../../common/services/AppConfigContext';
import { AuthUserContext } from '../../../common/services/AuthUserContext';
import { FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { bind } from '../../../common/utils/decorators';
import { SuccessNotification } from '../../../common/utils/notifications/Notification';
import { FeatureFlagsType, getFeatureFlag } from '../../../config/launch-darkly/FeatureFlagsController';
import { ModalManager } from '../../../modal-flows/services/ModalManager';
import { PATHS } from '../../../routing/routes';
import { OrganizationUsersContextType } from '../common/contexts/OrganizationUsersContext';
import { UserLevel, getUserLevelLabel } from '../enums/UserLevel';
import { UserOrigin, getUserOriginLabel } from '../enums/UserOrigin';
import { UserRole, getUserRoleLabel } from '../enums/UserRole';
import { User } from '../models/User';
import { OrganizationUsersRepository } from '../services/OrganizationUsersRepository';

type Props = WithRouterProps<OrganizationUsersContextType> & {};

type State = PaginatedTableState & {};

class Controller extends PaginatedTableController<User, Props, State> {
  private readonly usersRepository = new OrganizationUsersRepository();
  private readonly modalManager = new ModalManager();
  private readonly useRoles = Boolean(getFeatureFlag(FeatureFlagsType.ENABLE_ORGANIZATION_USER_ROLES));

  componentDidUpdate(prevProps: Props, prevState: State) {
    super.componentDidUpdate(prevProps, prevState);

    if (prevProps.context.nonce !== this.props.context.nonce) {
      this.reloadTableContent();
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.usersRepository.cancelAllRequests();
  }

  protected doInitialDataFetch(): void {
    // do not fetch until permissions are checked
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const permission = await this.permissionsService.canAccessOrganizationUsers();
    if (!permission) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }

    super.doInitialDataFetch();
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<User>> {
    try {
      const response = await this.usersRepository.getUsers(cursorInfo, this.getFilters(), this.getSorters());

      const userIds = response.data.map((u) => u.getId());
      await this.permissionsService.bulkPrefetchUserPermissions(userIds);

      return response;
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  protected getAdditionalDataGridProps(): Partial<DataGridProProps<User>> {
    return {
      ...super.getAdditionalDataGridProps(),
      getRowId: (user) => `${user.getId()}_${user.getPreferredName()}_${user.getLevel()}__${user.getRoles().join('_')}`,
    };
  }

  protected getFieldConfigurations(): FieldConfiguration<User>[] {
    const isCurrentUser = (user: User) => user.getId() === AuthUserContext.getUser().getId();
    const columns: FieldConfiguration<User>[] = [
      {
        fieldKey: 'name',
        label: t('username'),
        sortable: true,
        gqlKeys: { sorterField: 'name' },
        render: (item, { hasFocus }) => {
          const value = item.getUsername();
          if (!isCurrentUser(item)) {
            return <TypographyOverflow hasFocus={hasFocus}>{value}</TypographyOverflow>;
          }
          return (
            <Stack sx={{ width: 1, alignItems: 'center' }} direction="row" spacing={1}>
              <TypographyOverflow hasFocus={hasFocus}>{value}</TypographyOverflow>
              <Chip color="primary" size="small" label={t('you')} />
            </Stack>
          );
        },
      },
      {
        fieldKey: 'preferredName',
        label: t('preferredName'),
        sortable: true,
        gqlKeys: { sorterField: 'preferredName' },
        render: (item, { hasFocus }) => {
          const value = item.getPreferredName();
          if (!value) {
            return <BlankCell blankText={t('notSet')} />;
          }
          return <TypographyOverflow hasFocus={hasFocus}>{value}</TypographyOverflow>;
        },
      },
    ];
    if (this.useRoles) {
      columns.push({
        fieldKey: 'roles',
        label: t('userRoles.description'),
        columnProps: { minWidth: 200, maxWidth: 600 },
        sortable: false,
        render: (item) => {
          const roles = item.getRoles().map(getUserRoleLabel);
          if (roles.length === 0) {
            return <BlankCell blankText={t('none')} />;
          }
          return <TagCell tags={roles} />;
        },
      });
    } else {
      columns.push({
        fieldKey: 'level',
        label: t('userLevels.description'),
        columnProps: { maxWidth: 300 },
        sortable: true,
        gqlKeys: { sorterField: 'level' },
        render: (item) => {
          return <Chip label={getUserLevelLabel(item.getLevel())} />;
        },
      });
    }
    columns.push(
      {
        fieldKey: 'origin',
        label: t('userOrigins.description'),
        sortable: true,
        gqlKeys: { sorterField: 'origin' },
        columnProps: { maxWidth: 300 },
        render: (item) => {
          return <Chip label={getUserOriginLabel(item.getOrigin())} />;
        },
      },
      {
        fieldKey: 'actions',
        label: '',
        description: t('a11y.organization.users.actions'),
        columnProps: { maxWidth: 60, minWidth: 60 },
        render: (item, { tabIndex }) => {
          const isCurrent = isCurrentUser(item);
          const isWebAccount = isCurrent && item.getOrigin() === UserOrigin.WEB_ACCOUNT;
          const webAccountConfig = AppConfigContext.getPresets().account;
          const useWebAccount = isWebAccount && webAccountConfig !== null;
          return (
            <MenuCell
              tabIndex={tabIndex}
              actions={[
                {
                  label: t('edit'),
                  isActive: () => this.permissionsService.canUpdateUser(item.getId()),
                  handler: () => this.handleUpdateUser(item),
                },
                {
                  label: useWebAccount ? (
                    <Link target="_blank" href={webAccountConfig.changePassword} underline="none" color="text.primary">
                      {t('accountButtonChildren.changePassword')}
                    </Link>
                  ) : (
                    t('accountButtonChildren.changePassword')
                  ),
                  isActive: () => {
                    if (!isCurrent) {
                      // `canUpdatePassword` does not provide a `userId` argument as it is
                      // explicitly targeted to the current user. Handle this case manually.
                      return Promise.resolve(false);
                    }
                    if (useWebAccount) {
                      // WebAccount users can always change their password on WebAccount if available
                      return Promise.resolve(true);
                    }
                    return this.permissionsService.canChangePassword();
                  },
                  handler: useWebAccount ? () => {} : () => this.handleChangePassword(item),
                },
                {
                  label: t('resetPassword'),
                  isActive: () => this.permissionsService.canResetPassword(item.getId()),
                  handler: () => this.handleResetPassword(item),
                },
                {
                  label: t('delete'),
                  isActive: () => this.permissionsService.canDeleteUser(item.getId()),
                  handler: () => this.handleDeleteUser(item),
                },
              ]}
            />
          );
        },
      }
    );
    return columns;
  }

  protected shouldStoreFiltersInUrl(): boolean {
    return true;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    const filters: FilterConfiguration[] = [
      {
        filterKey: 'name',
        label: t('username'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
      {
        filterKey: 'preferredName',
        label: t('preferredName'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
    ];
    if (!this.useRoles) {
      filters.push({
        filterKey: 'level',
        label: t('userLevels.description'),
        type: FilterValueInputType.SELECT,
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
        options: Object.values(UserLevel).map((v) => ({ value: v, label: getUserLevelLabel(v) })),
      });
    } else {
      filters.push({
        filterKey: 'role',
        label: t('userRoles.description'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.CONTAINS, label: t('filterLabels.has') }],
        options: Object.values(UserRole).map((r) => ({ value: r, label: getUserRoleLabel(r) })),
      });
    }
    filters.push({
      filterKey: 'origin',
      label: t('userOrigins.description'),
      type: FilterValueInputType.SELECT,
      operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
      options: Object.values(UserOrigin).map((o) => ({ value: o, label: getUserOriginLabel(o) })),
    });
    return filters;
  }

  @bind
  private async handleUpdateUser(user: User) {
    const selfUpdate = user.getId() === AuthUserContext.getUser().getId();
    const { userId } = await this.modalManager.updateUser(user.getId(), user.getPreferredName(), user.getLevel(), user.getRoles(), selfUpdate);

    if (!userId) return;

    this.showMessage(new SuccessNotification(t('updateUser.success', { username: user.getUsername() })));
    this.reloadTableContent();
  }

  @bind
  private async handleDeleteUser(user: User) {
    const { userId } = await this.modalManager.deleteUser(user.getId(), user.getUsername());

    if (!userId) return;

    this.showMessage(new SuccessNotification(t('deleteUser.success', { username: user.getUsername() })));
    this.reloadTableContent();
  }

  @bind
  private async handleChangePassword(user: User) {
    const { changed } = await this.modalManager.changePassword(user.getUsername());
    if (!changed) return;

    this.showMessage(new SuccessNotification(t('changeOwnPassword.success')));
  }

  @bind
  private async handleResetPassword(user: User) {
    await this.modalManager.resetUserPassword(user.getId(), user.getUsername());
  }
}

export const UsersListController = withRouter<OrganizationUsersContextType>(Controller);
